<template>
  <v-container class="member-affiliates-container pa-4 pa-md-8">
    <!-- Encabezado + barra de acciones -->
    <v-row
      justify="space-between"
      align="center"
      class="header-row flex-wrap mb-4"
    >
      <v-col cols="12" md="auto" class="pb-0 pb-md-4">
        <h2 class="wc-h3 text-primary-variant mb-1">Gestión de Submiembros</h2>
        <p class="wc-body-2 text-disabled mb-0">
          Administración y visualización de todos los submiembros
          organizacionales.
        </p>
      </v-col>

      <v-col
        cols="12"
        md="auto"
        class="d-flex align-center justify-end flex-wrap actions-bar"
      >
        <!-- Buscador -->
        <div class="searchbar d-flex flex-row align-center mr-2 mb-2 mb-md-0">
          <v-btn
            fab
            small
            elevation="0"
            class="rounded-pill rounded-r-0"
            color="#feb300"
            @click="onSearch"
          >
            <v-icon size="20">mdi-magnify</v-icon>
          </v-btn>
          <input
            type="text"
            placeholder="Buscar submiembros..."
            class="rounded-pill rounded-l-0"
            v-model="pagination.name"
            v-on:keypress.enter="onSearch"
          />
        </div>

        <!-- Filtro de estatus -->
        <v-select
          solo
          dense
          flat
          clearable
          placeholder="Estatus"
          hide-details="auto"
          class="rounded-pill status-select mr-2 mb-2 mb-md-0"
          background-color="#f0f1f4"
          :items="statusItems"
          :color="color.primaryVariant"
          :item-color="color.primaryVariant"
          v-model="pagination.validationStatus"
          @change="onSearch"
        />

        <!-- Export Data -->
        <v-btn
          outlined
          rounded
          elevation="0"
          :color="color.primaryVariant"
          class="mr-2 mb-2 mb-md-0 export-btn"
          :loading="exporting"
          @click="exportData"
        >
          <v-icon left>mdi-download</v-icon>
          <span class="text-none font-weight-bold">Exportar Datos</span>
        </v-btn>

        <!-- Add Member -->
        <v-btn
          rounded
          elevation="0"
          :color="color.secondary"
          :disabled="hasPassedAffiliationLimit"
          class="add-btn mb-2 mb-md-0"
          to="/member/newAffiliate/1"
        >
          <v-icon left>mdi-plus</v-icon>
          <span class="text-none font-weight-bold">Agregar Submiembro</span>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Paginación superior -->
    <MemberPagination
      v-if="affiliates.length > 0"
      class="mb-4"
      :page="pagination.page"
      :length="pagination.totalPages || 1"
      :total-docs="pagination.totalDocs"
      :current-items="affiliates.length"
      @input="onPageChange"
    />

      <!-- Listado de tarjetas -->
    <div v-if="affiliates.length > 0" class="members-list">
      <AffiliateSheet
        v-for="(affiliate, i) in affiliates"
        :key="affiliate._id || i"
        :affiliate="affiliate"
        :member-fields="memberFields"
        :org-form-sections="orgFormSections"
        @delete="handleDelete"
        @edit="openEditModal"
      ></AffiliateSheet>
    </div>

    <!-- Empty state -->
    <v-card
      v-else
      elevation="1"
      rounded="lg"
      class="empty-state pa-10 text-center"
    >
      <v-icon size="56" color="#8c929e">mdi-account-off</v-icon>
      <p class="wc-body-1 text-disabled mt-3 mb-0">
        No se encontraron submiembros.
      </p>
    </v-card>

    <!-- Paginación inferior -->
    <MemberPagination
      v-if="affiliates.length > 0"
      class="mt-4"
      :page="pagination.page"
      :length="pagination.totalPages || 1"
      :total-docs="pagination.totalDocs"
      :current-items="affiliates.length"
      @input="onPageChange"
    />

    <!-- Modal editar -->
    <ModalTemplate
      @close="closeModal"
      @continue="handleContinue"
      :open="openModal"
      :primaryBtnProps="{
        text: 'Guardar',
        show: true,
        class: 'ml-2',
      }"
      :secondaryBtnProps="{
        text: 'Cerrar',
        show: true,
      }"
    >
      <template #content>
        <div class="d-flex flex-column align-stretch">
          <p class="wc-h3 text-primary-variant align-self-center my-6">
            {{ formCreate ? 'Agregar Afiliado' : 'Actualizar Submiembro' }} 
          </p>
          <v-form ref="newAffiliateForm">
            <v-text-field
              v-model="newAffiliate.user.name"
              label="Nombre"
              filled
              rounded
            />
            <v-text-field
              v-model="newAffiliate.user.firstLastname"
              label="Apellido"
              filled
              rounded
            />
            <v-text-field
              v-model="newAffiliate.user.email"
              label="Correo Electrónico"
              type="email"
              filled
              rounded
            />
            <v-text-field
              v-model="newAffiliate.user.position"
              label="Cargo"
              filled
              rounded
            />

            <template v-if="dynamicFormFields.length > 0">
              <div class="d-flex align-center my-2">
                <v-divider></v-divider>
                <span class="caption grey--text text--darken-1 mx-2">
                  Campos adicionales
                </span>
                <v-divider></v-divider>
              </div>

              <div
                v-for="field in dynamicFormFields"
                :key="field._id || field.formattedName"
              >
                <v-select
                  v-if="field.fieldType === 'LIST'"
                  :items="field.options"
                  :multiple="field.allowMultiple"
                  v-model="customFieldsData[field.formattedName]"
                  :label="field.name + (field.required ? ' *' : '')"
                  filled
                  rounded
                />
                <v-text-field
                  v-else-if="field.fieldType === 'NUMBER'"
                  type="number"
                  v-model="customFieldsData[field.formattedName]"
                  :label="field.name + (field.required ? ' *' : '')"
                  filled
                  rounded
                />
                <v-text-field
                  v-else-if="field.fieldType === 'DATE'"
                  type="date"
                  v-model="customFieldsData[field.formattedName]"
                  :label="field.name + (field.required ? ' *' : '')"
                  filled
                  rounded
                />
                <v-text-field
                  v-else
                  v-model="customFieldsData[field.formattedName]"
                  :label="field.name + (field.required ? ' *' : '')"
                  filled
                  rounded
                />
              </div>
            </template>
          </v-form>
        </div>
      </template>
    </ModalTemplate>

    <!-- Dialog eliminar -->
    <v-dialog v-model="deleteAffiliateDialog" width="600">
      <DeleteDialog
        @close="deleteAffiliateDialog = false"
        @continue="DeleteAffiliate"
        :affiliate="affiliateToDelete"
        :loading="loadingDelete"
      ></DeleteDialog>
    </v-dialog>
  </v-container>
</template>
<script>
import ActionButton from '../../components/shared/ActionButton.vue';
import AffiliateSheet from '../../components/memberDashboard/affiliates/affiliateSheet.vue';
import DeleteDialog from '../../components/memberDashboard/affiliates/DeleteDialog.vue';
import MemberPagination from '../../components/memberDashboard/affiliates/MemberPagination.vue';
import ModalTemplate from '../../components/shared/ModalTemplate.vue';
import { mapActions, mapState, mapMutations } from 'vuex';
import { colors } from '@/constants/colors.js';
import { homologateStatusWithColor } from '@/common/hook/useHelper.js';
import { resolveMemberAffiliateBranch } from '@/common/memberDashboardRouting';
import * as XLSX from 'xlsx/xlsx.mjs';

export default {
  components: {
    ActionButton,
    AffiliateSheet,
    MemberPagination,
    ModalTemplate,
    DeleteDialog,
  },
  data() {
    return {
      busqueda: '',
      affiliates: [],
      memberFields: [],
      orgFormSections: [],
      openModal: false,
      newAffiliate: {
        user: {
          name: '',
          firstLastname: '',
          email: '',
          position: '',
        },
      },
      customFieldsData: {},
      formCreate: true,
      color: colors,
      exporting: false,
      pagination: {
        page: 1,
        limit: 10,
        totalPages: 0,
        totalDocs: 0,
        name: '',
        branch: '',
        validationStatus: '',
      },
      statusItems: [
        { text: 'Prospecto', value: 'PROSPECTO' },
        { text: 'Activo', value: 'ACTIVE' },
        { text: 'Inactivo', value: 'INACTIVO' },
        { text: 'Expirado', value: 'EXPIRED' },
      ],
      selectedStatus: '',
      deleteAffiliateDialog: false,
      affiliateToDelete: {},
      loadingDelete: false,
    };
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapState('membershipProfile', ['userMembership']),
    isCanacoLeonMember() {
      const canacoLeonId = '6500e21c80d167001bf44b63';
      return this.user.headOffice._id == canacoLeonId;
    },
    isActiveAddNewSubMember() {
      if (this.user.role == 'MEMBER') {
        return false;
      }
      return false;
    },
    dynamicFormFields() {
      const memberData = Array.isArray(this.newAffiliate.user.memberData)
        ? this.newAffiliate.user.memberData
        : [];
      const defByFmt = {};
      (this.memberFields || []).forEach((f) => {
        defByFmt[f.formattedName] = f;
      });
      return memberData
        .filter((field) => field.fieldType !== 'DOC')
        .map((field) => {
          const def = defByFmt[field.formattedName] || {};
          return {
            _id: field._id || def._id,
            formattedName: field.formattedName,
            name: field.name || def.name || field.formattedName,
            fieldType: field.fieldType || def.fieldType || 'TEXT',
            options: field.options || def.options || [],
            allowMultiple:
              field.allowMultiple != null
                ? field.allowMultiple
                : def.allowMultiple || false,
            required:
              field.required != null ? field.required : def.required || false,
          };
        });
    },
    hasPassedAffiliationLimit() {
      const { affiliationType: nationalAffiliation } =
        this.user?.affiliation?.national?.details[0] || {};
      const isSumbembersLimitActive = !!nationalAffiliation?.submembersLimit?.active;

      if (!isSumbembersLimitActive) { // if false, member used can add unlimited submembers
        return false;
      }
      return this.pagination.totalDocs >= nationalAffiliation.submembersLimit?.value;
    },
  },
  methods: {
    ...mapActions('memberAffiliates', [
      'getAffiliates',
      'addAffiliate',
      'deleteAffiliate',
      'updateAffiliate',
      'exportAffiliates',
    ]),
    ...mapActions('user', ['realoadUserData']),
    ...mapMutations('loader', ['loading', 'loaded']),
    ...mapMutations('notification', ['show']),
    ...mapActions('registrationFields', ['getRegistrationFields']),
    ...mapActions('formSections', ['getFormSectionsByOrg']),

    handleDelete(affiliate) {
      this.affiliateToDelete = affiliate;
      this.deleteAffiliateDialog = true;
    },
    openEditModal(affiliate) {
      let affiliateCopy = JSON.parse(JSON.stringify(affiliate));
      this.openModal = true;
      this.formCreate = false;
      this.newAffiliate.user = affiliateCopy;
      this.customFieldsData = {};
      const memberData = Array.isArray(affiliateCopy.memberData)
        ? affiliateCopy.memberData
        : [];
      memberData.forEach((field) => {
        if (field.fieldType === 'DOC') return;
        this.$set(this.customFieldsData, field.formattedName, field.value);
      });
    },
    closeModal() {
      this.openModal = false;
      this.newAffiliate.user = {
        name: '',
        firstLastname: '',
        email: '',
        position: '',
      };
      this.customFieldsData = {};
    },
    async handleContinue() {
      if (this.formCreate) {
        this.AddAffiliate();
      } else {
        this.UpdateAffiliate();
      }
    },
    async AddAffiliate() {
      try {
        this.loading();
        const affiliate = await this.addAffiliate(this.newAffiliate);
        if (affiliate.status) {
          this.closeModal();
        } else {
          this.show({
            text: affiliate.message,
            color: 'red',
          });
          console.log(affiliate.message);
        }
        this.loaded();
      } catch (error) {
        console.log(error);
      } finally {
        await this.realoadUserData({ email: this.user.email });
      }
    },
    syncCustomFieldsToMemberData() {
      const user = this.newAffiliate.user;
      if (!Array.isArray(user.memberData)) return;
      user.memberData.forEach((entry) => {
        if (entry.fieldType === 'DOC') return;
        if (
          Object.prototype.hasOwnProperty.call(
            this.customFieldsData,
            entry.formattedName
          )
        ) {
          entry.value = this.customFieldsData[entry.formattedName];
        }
      });
    },
    async UpdateAffiliate() {
      this.syncCustomFieldsToMemberData();
      const payload = JSON.parse(JSON.stringify(this.newAffiliate));
      this.closeModal();
      try {
        const updatedAffiliate = await this.updateAffiliate(payload);
        if (updatedAffiliate.status) {
          this.affiliates = this.affiliates.map((affiliate) =>
            affiliate._id == updatedAffiliate.affiliate._id
              ? updatedAffiliate.affiliate
              : affiliate
          );
          this.show({
            text: 'Submiembro actualizado correctamente',
            color: 'success',
          });
        } else {
          this.show({
            text:
              updatedAffiliate.message || 'Error al actualizar el submiembro',
            color: 'red',
          });
        }
      } catch (error) {
        console.log(error);
        this.show({
          text: 'Error al actualizar el submiembro',
          color: 'red',
        });
      } finally {
        await this.realoadUserData({ email: this.user.email });
      }
    },
    async DeleteAffiliate(affiliate) {
      try {
        this.loadingDelete = true;
        const affiliateToDelete = await this.deleteAffiliate(affiliate);
        if (affiliateToDelete.status) {
          this.affiliates = this.affiliates.filter(
            (a) => a._id !== affiliate._id,
          );
          this.pagination.totalDocs = Math.max(
            0,
            this.pagination.totalDocs - 1,
          );
        } else {
          console.log(affiliateToDelete.message);
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loadingDelete = false;
        this.deleteAffiliateDialog = false;
        await this.realoadUserData({ email: this.user.email });
        await this.getFiltereAffiliates();
      }
    },
    onSearch() {
      this.pagination.page = 1;
      this.getFiltereAffiliates();
    },
    async getOrganizationConfig() {
      try {
        const orgId = !!this.userMembership
          ? this.userMembership.organization._id
          : this.user.branchId;
        if (!orgId) return;
        const [fieldsRes, sectionsRes] = await Promise.all([
          this.getRegistrationFields(orgId),
          this.getFormSectionsByOrg(orgId),
        ]);
        if (fieldsRes && fieldsRes.status) {
          this.memberFields = fieldsRes.data.data || [];
        }
        if (sectionsRes && sectionsRes.status) {
          this.orgFormSections = sectionsRes.data.sections || [];
        }
      } catch (error) {
        console.log('ERROR getOrganizationConfig - ', error);
      }
    },
    onPageChange(p) {
      this.pagination.page = p;
      this.getFiltereAffiliates();
    },
    async getFiltereAffiliates() {
      try {
        this.loading();
        this.pagination.branch =
          resolveMemberAffiliateBranch(this.user, this.userMembership) || '';
        const affiliates = await this.getAffiliates(this.pagination);

        if (affiliates.status) {
          this.affiliates = affiliates.affiliates.data.members;
          this.pagination.totalPages = affiliates.affiliates.data.totalPages;
          this.pagination.totalDocs = affiliates.affiliates.data.totalDocs || 0;
        } else {
          console.log(affiliates.message);
        }
      } catch (error) {
        console.log('ERROR - ', error);
      } finally {
        this.loaded();
      }
    },
    async exportData() {
      try {
        this.exporting = true;
        this.loading();
        const branch =
          resolveMemberAffiliateBranch(this.user, this.userMembership) || '';
        const result = await this.exportAffiliates({
          name: this.pagination.name,
          validationStatus: this.pagination.validationStatus,
          branch,
        });

        if (result.status) {
          const rows = result.members.map((m) => {
            const row = {
              Nombre: m.name || '',
              Apellido: m.firstLastname || '',
              'Correo Electrónico': m.email || '',
              Cargo: m.position || 'N/D',
              'F. Inicio': m.createdAt
                ? this.moment(m.createdAt).format('YYYY-MM-DD')
                : 'N/D',
              Estado: homologateStatusWithColor(m.validationStatus).status,
              Membresía: m.branch || m.company || 'N/D',
            };
            if (m.memberData && m.memberData.length) {
              m.memberData.forEach((f) => {
                if (
                  f.name &&
                  f.value !== null &&
                  f.value !== undefined &&
                  f.value !== ''
                ) {
                  row[f.name] = Array.isArray(f.value)
                    ? f.value.join(', ')
                    : f.value;
                }
              });
            }
            return row;
          });

          const ws = XLSX.utils.json_to_sheet(rows);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Submiembros');
          XLSX.writeFile(wb, 'submiembros.xlsx');

          this.show({
            text: `Se exportaron ${rows.length} submiembros.`,
            color: 'success',
          });
        } else {
          this.show({
            text: 'No fue posible exportar los datos.',
            color: 'red',
          });
        }
      } catch (error) {
        console.log('ERROR export - ', error);
        this.show({ text: 'No fue posible exportar los datos.', color: 'red' });
      } finally {
        this.exporting = false;
        this.loaded();
      }
    },
  },
  async mounted() {
    await this.getOrganizationConfig();
    await this.getFiltereAffiliates();
  },
};
</script>

<style>
.member-affiliates-container {
  background-color: #f9fafc;
}

.searchbar input::placeholder {
  font-size: 15px;
  color: #9b9f9b;
}

.searchbar input {
  padding-left: 20px;
  width: 18rem;
  max-width: 18rem;
  min-width: 8rem;
  border: 0;
  border-color: transparent;
  background-color: #f0f1f4;
  height: 38px;
}

.searchbar input:focus {
  outline: none;
  border: 1px solid transparent;
}

.status-select {
  max-width: 160px;
}

.export-btn,
.add-btn {
  height: 38px !important;
}

.members-list {
  display: flex;
  flex-direction: column;
}

.empty-state {
  background-color: #ffffff;
}

@media (max-width: 960px) {
  .actions-bar {
    justify-content: flex-start !important;
  }
  .searchbar input {
    width: 100%;
    max-width: 100%;
  }
}
</style>
